/** URL segments that are app routes, not location slugs (first path segment). */
export const RESERVED_FIRST_SEGMENTS = new Set([
  'dashboard', 'admin', 'login', 'signup', 'search', 'account', 'notifications',
  'org', 'organizations', 'organization-types', 'locations', 'pricing', 'checkout',
  'about', 'privacy', 'terms', 'contact', 'roadmap', 'pending-approval',
  'email-verification', 'password-reset', 'request-password-reset', 'oauth', 'file-upload',
  'notices', 'events', 'facilities', 'tenants', 'buy-sell', 'food-court', 'lost-found',
  'services', 'information-desk', 'request-offer', 'transportation', 'meet-the-team',
  'p', 'tools', 'test-dashboard', 'reception',
]);

/** Second-segment values that indicate a location-scoped section listing/detail page. */
export const LOCATION_SECTIONS = new Set([
  'about', 'notices', 'events', 'tenants', 'food-court', 'facilities', 'services',
  'buy-sell', 'request-offer', 'lost-found', 'transportation', 'information-desk',
  'retail', 'housing', 'professionals',
]);

export type LocationSlugSource = {
  locationCode?: string | null;
  slug?: string | null;
};

/** Match a location from URL segment (same rules as getLocationBySlug). */
export function findLocationBySlug<T extends LocationSlugSource>(
  locations: T[],
  slug: string
): T | undefined {
  return locations.find(
    (loc) => loc.slug === slug || loc.locationCode === slug
  );
}

/** Read location slug from `/:slug/...` style paths. */
export function getLocationSlugFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (!segments.length) return null;
  const first = segments[0];
  if (RESERVED_FIRST_SEGMENTS.has(first)) return null;
  if (segments.length === 1) return first;
  if (segments.length >= 2 && LOCATION_SECTIONS.has(segments[1])) return first;
  return null;
}

/** Slug for `/:slug/section` URLs — prefers taxonomy slug (never internal locationCode). */
export function locationUrlSlug(location?: LocationSlugSource | null): string | undefined {
  return location?.slug ?? undefined;
}

/** Build a location-scoped listing path from a location entity. */
export function locationSectionPathFromLocation(
  location: LocationSlugSource | null | undefined,
  section: string,
  ...rest: string[]
): string {
  return locationSectionPath(locationUrlSlug(location), section, ...rest);
}

/** Resolve the slug used in sidebar links and location-scoped navigation. */
export function resolveLocationSlug(
  pathname: string,
  paramsSlug?: string | null,
  selectedLocation?: LocationSlugSource | null
): string | undefined {
  return (
    getLocationSlugFromPath(pathname) ??
    paramsSlug ??
    selectedLocation?.slug ??
    selectedLocation?.locationCode ??
    undefined
  );
}

/** Build `/:slug/section/...` or `/section/...` when no location context. */
export function locationSectionPath(
  locationSlug: string | undefined,
  section: string,
  ...rest: string[]
): string {
  const parts = rest.filter(Boolean);
  const suffix = parts.length ? `/${parts.join('/')}` : '';
  if (locationSlug) return `/${locationSlug}/${section}${suffix}`;
  return `/${section}${suffix}`;
}

/** Prefix a section path with the location slug when applicable, e.g. `/buy-sell` → `/areaxo/buy-sell`. */
export function locationScopedPath(
  location: LocationSlugSource | null | undefined,
  path: string
): string {
  const slug = locationUrlSlug(location);
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (!slug) return normalized;

  const firstSegment = normalized.split('/').filter(Boolean)[0];
  if (firstSegment && LOCATION_SECTIONS.has(firstSegment)) {
    return `/${slug}${normalized}`;
  }
  return normalized;
}
